import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { cx } from '@platform/core/lib/cx';
import { routes } from '@platform/core/lib/routes';
import { ModuleTile } from '@platform/ui';
import { useWorkspaceMeta } from '../../shell/useWorkspaceMeta';
import { SysTable } from '../system/SysTable';
import { SystemPage } from '../system/SystemPage';
import { formStats } from './formStats';
import { formTitle } from './formTitle';
import { useFormListRows } from './useFormListRows';
import './customize.css';

const COLUMNS = 'minmax(180px, 1.5fr) minmax(170px, 1.3fr) minmax(110px, 0.8fr) 70px 100px 56px 120px 110px';
const BADGE = { ws: 'sysCust.forms.workspace', global: 'sysCust.forms.global', std: 'sysCust.forms.standard' } as const;

/**
 * «Форми» (прототип v4 G:L9339–9428, pgCustForms): пошук форм, «N форм · пояснення рівня», таблиця «Форма «Справа» · модуль · тип запису ·
 * групи · розміщено полів («13 з 19») · вкладки · розміщення · Налаштувати» — у просторі форма кожного реалізованого типу запису в порядку
 * модулів простору, на рівні «Глобально» — 169 форм шаблонів у порядку каталогу (useFormListRows); «Модуль» — таблиця як модуль прототипу
 * (головна — назва модуля, дочірня — власна назва й плитка); групи, розміщені поля, вкладки — формули прототипу cfRows (formStats);
 * «Розміщення» — «Робочий простір» (власна форма простору: засіяні картки парламенту, засіяний макет реєстрів, форма редактора, відмінна від
 * стандартного макета), «Глобально» (успадкована глобальна форма, ADR-027) чи «Стандартний» — те саме правило, що на сторінці модуля й у
 * редакторі форми (wsFormSource); рядок лічильника — пояснення рівня, як у прототипі (cfScope).
 * Рядок і «Налаштувати» відкривають редактор форми типу запису в макеті «Налаштування» (прототип v4 cust-form без inMod: «Форми» в меню
 * активні, крихти «… › Налаштування › Форми › Форма «Справа»», «← Форми»; дочірня таблиця — ?type=); рівень редактора — рівень сторінки.
 */
export function CustFormsPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const { ws } = useWorkspaceParams();
  const { workspace } = useWorkspaceMeta(ws);
  const [q, setQ] = useState('');
  const { rows: all, loaded, global } = useFormListRows(ws);
  const query = q.trim().toLocaleLowerCase();
  const rows = all
    .filter((s) => !query || `${s.name} ${label(s.row.rt.singular ?? s.row.rt.name)}`.toLocaleLowerCase().includes(query))
    .map(({ row: { def: d, rt }, name, tile, source }) => {
      const stats = formStats(rt);
      const to = routes.customizeForm(d.module.key, rt.main ? undefined : rt.key);
      return {
        key: `${d.module.key}/${rt.key}`,
        onClick: () => navigate(to),
        cells: [
          <span key="f" className="sp-strong">{formTitle(rt, label, t)}</span>,
          <span key="m" className="sp-who cu-formmod"><ModuleTile icon={tile.icon} tone={tile.tone} size={22} /><span className="cu-ellipsis sp-muted">{name}</span></span>,
          label(rt.singular ?? rt.name),
          <span key="g" className="sp-mono">{stats.groups}</span>,
          <span key="p" className="sp-mono">{t('sysCust.forms.placedOf', { placed: stats.placed, total: stats.total })}</span>,
          <span key="t" className="sp-mono">{stats.tabs}</span>,
          <span key="s" className={cx('cu-badge', source !== 'std' && `cu-badge--${source}`)}>{t(BADGE[source])}</span>,
          <span key="a" className="sp-actions"><Link className="sp-btn sp-btn--accent cu-configure" to={to} onClick={(e) => e.stopPropagation()}>{t('sysCust.forms.configure')}</Link></span>,
        ],
      };
    });
  const head = ['form', 'module', 'type', 'groups', 'placed', 'tabs', 'placement', ''].map((k) => (k ? t(`sysCust.forms.${k}`) : ''));
  return (
    <SystemPage className="cu-gap16" title={t('sysPages.sections.forms')} sub={global ? t('sysCust.forms.subGlobal') : t('sysCust.forms.subWs', { ws: workspace?.name ?? '' })}>
      <div className="sp-toolbar cu-toolbar">
        <input className="sp-input cu-search" value={q} placeholder={t('sysCust.forms.search')} aria-label={t('sysCust.forms.search')} onChange={(e) => setQ(e.target.value)} />
        <span className="sp-count">{loaded ? `${t('sysCust.forms.count', { count: rows.length })} · ${t(global ? 'sysCust.forms.scopeGlobal' : 'sysCust.forms.scopeWs')}` : '…'}</span>
      </div>
      <SysTable columns={COLUMNS} minWidth={980} head={head} rows={rows} empty={loaded ? t('sysCust.forms.none') : undefined} label={t('sysPages.sections.forms')} />
    </SystemPage>
  );
}
